import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// AvatarPicker.tsx -- avatar picker for both Agents and Co-Workers.
//
// Per workbench F.4 ("Agents & Co-Workers" umbrella): both AI personas
// (Agents) and human peers (Co-workers) share the same avatar surface.
// v1 supports emoji selection only; future enhancements:
//   - drag-in image upload from desktop
//   - AI-generate from a description
//
// Shapes:
//   - Trigger: a button showing the currently-selected avatar at chip
//     size. Click to open the popover.
//   - Popover: search input at the top, then a scrollable category-
//     sectioned grid of emoji buttons. Click one to pick + close.
//   - (Future tabs at the top: "Emoji" / "Upload" / "AI generate")
//
// We use a curated subset (~200 emoji) rather than the full Unicode
// emoji set. The curation skews toward roles, work-relevant tools,
// animals as mascots, and a handful of expressive faces. Each emoji
// has a search-friendly name so typing 'lab' filters to the lab
// coat / scientist set, etc.
import { useEffect, useMemo, useRef, useState } from 'react';
// Curated set. Categories ordered by likely-most-used first.
// Each entry is one row in the grid; tags are space-separated keywords.
const CATEGORIES = [
    {
        name: 'Roles & people',
        emojis: [
            { ch: '\uD83E\uDD16', name: 'robot', tags: 'robot bot ai assistant default' },
            { ch: '\uD83E\uDDD1\u200D\uD83D\uDCBB', name: 'technologist', tags: 'developer engineer coder programmer tech laptop' },
            { ch: '\uD83E\uDDD1\u200D\uD83D\uDCBC', name: 'office worker', tags: 'office worker corporate suit' },
            { ch: '\uD83E\uDDD1\u200D\uD83D\uDD2C', name: 'scientist', tags: 'scientist lab researcher microscope' },
            { ch: '\uD83E\uDDD1\u200D\uD83C\uDFA8', name: 'artist', tags: 'artist designer creative palette' },
            { ch: '\uD83E\uDDD1\u200D\uD83C\uDFEB', name: 'teacher', tags: 'teacher educator instructor mentor' },
            { ch: '\uD83E\uDDD1\u200D\uD83D\uDD27', name: 'mechanic', tags: 'mechanic engineer wrench technician' },
            { ch: '\uD83E\uDDD1\u200D\u2696\uFE0F', name: 'judge', tags: 'judge lawyer legal scales justice' },
            { ch: '\uD83E\uDDD1\u200D\uD83C\uDF73', name: 'cook', tags: 'cook chef kitchen' },
            { ch: '\uD83E\uDDD1\u200D\uD83C\uDF93', name: 'student', tags: 'student graduate academic' },
            { ch: '\uD83D\uDD75\uFE0F', name: 'detective', tags: 'detective investigator research search sleuth' },
            { ch: '\uD83E\uDDD9', name: 'mage', tags: 'mage wizard magic prompt' },
            { ch: '\uD83E\uDD2F', name: 'mind blown', tags: 'mind blown wow surprised insight' },
            { ch: '\uD83E\uDD13', name: 'nerd', tags: 'nerd glasses smart studious' },
            { ch: '\uD83D\uDE0E', name: 'sunglasses', tags: 'cool sunglasses confident' },
            { ch: '\uD83E\uDD23', name: 'rofl', tags: 'laugh funny joke comedy' },
            { ch: '\uD83E\uDDB8', name: 'superhero', tags: 'superhero hero strong' },
            { ch: '\uD83E\uDDDE', name: 'genie', tags: 'genie magical wishes' },
            { ch: '\uD83E\uDD20', name: 'cowboy', tags: 'cowboy yeehaw western' },
            { ch: '\uD83E\uDDD0', name: 'monocle', tags: 'monocle fancy inspector skeptic' },
        ],
    },
    {
        name: 'Work & tools',
        emojis: [
            { ch: '\uD83D\uDCBB', name: 'laptop', tags: 'laptop computer code dev' },
            { ch: '\uD83D\uDCDD', name: 'memo', tags: 'memo notes writing draft' },
            { ch: '\uD83D\uDCC8', name: 'chart up', tags: 'chart graph analytics growth metrics' },
            { ch: '\uD83D\uDCCA', name: 'bar chart', tags: 'bar chart finance data analysis financial' },
            { ch: '\uD83D\uDCC9', name: 'chart down', tags: 'chart down decline loss' },
            { ch: '\uD83D\uDCB0', name: 'money bag', tags: 'money finance budget cash payment' },
            { ch: '\uD83D\uDCB3', name: 'credit card', tags: 'credit card payment finance' },
            { ch: '\uD83D\uDCBC', name: 'briefcase', tags: 'briefcase business work professional' },
            { ch: '\uD83D\uDCC1', name: 'folder', tags: 'folder filing files documents' },
            { ch: '\uD83D\uDCC2', name: 'open folder', tags: 'open folder browse explore' },
            { ch: '\uD83D\uDCC3', name: 'page curl', tags: 'document page report' },
            { ch: '\uD83D\uDCC4', name: 'document', tags: 'document file paper' },
            { ch: '\uD83D\uDCD1', name: 'bookmark', tags: 'bookmark tabs reference' },
            { ch: '\uD83D\uDCDA', name: 'books', tags: 'books library research reference' },
            { ch: '\uD83D\uDD8A\uFE0F', name: 'pen', tags: 'pen writing edit author' },
            { ch: '\uD83D\uDD8C\uFE0F', name: 'paintbrush', tags: 'brush paint design creative' },
            { ch: '\uD83D\uDD27', name: 'wrench', tags: 'wrench tools fix repair admin' },
            { ch: '\uD83D\uDEE0\uFE0F', name: 'hammer wrench', tags: 'tools hammer build develop maintenance' },
            { ch: '\uD83D\uDD0D', name: 'magnifier', tags: 'search magnify find investigate research' },
            { ch: '\uD83D\uDCBE', name: 'floppy', tags: 'floppy save data storage' },
            { ch: '\uD83D\uDCC5', name: 'calendar', tags: 'calendar schedule date plan' },
            { ch: '\uD83D\uDCCB', name: 'clipboard', tags: 'clipboard list checklist tasks' },
            { ch: '\uD83D\uDCCC', name: 'pin', tags: 'pin pinned attach' },
            { ch: '\uD83D\uDCCE', name: 'paperclip', tags: 'paperclip attach link' },
            { ch: '\uD83E\uDDEE', name: 'abacus', tags: 'abacus math accounting calculator' },
        ],
    },
    {
        name: 'Animals',
        emojis: [
            { ch: '\uD83D\uDC36', name: 'dog', tags: 'dog puppy pet loyal' },
            { ch: '\uD83D\uDC31', name: 'cat', tags: 'cat kitten pet' },
            { ch: '\uD83D\uDC2F', name: 'tiger', tags: 'tiger fierce strong' },
            { ch: '\uD83E\uDD81', name: 'lion', tags: 'lion king strong leader' },
            { ch: '\uD83D\uDC2D', name: 'mouse', tags: 'mouse small detail' },
            { ch: '\uD83D\uDC30', name: 'rabbit', tags: 'rabbit bunny fast' },
            { ch: '\uD83E\uDD8A', name: 'fox', tags: 'fox clever sly cunning' },
            { ch: '\uD83D\uDC3B', name: 'bear', tags: 'bear strong cuddly' },
            { ch: '\uD83D\uDC3C', name: 'panda', tags: 'panda cute' },
            { ch: '\uD83E\uDD8B', name: 'butterfly', tags: 'butterfly transform graceful' },
            { ch: '\uD83D\uDC1D', name: 'bee', tags: 'bee busy productive' },
            { ch: '\uD83E\uDD86', name: 'duck', tags: 'duck quack' },
            { ch: '\uD83E\uDD89', name: 'owl', tags: 'owl wise night' },
            { ch: '\uD83E\uDD85', name: 'eagle', tags: 'eagle soar vision sharp' },
            { ch: '\uD83E\uDD8E', name: 'gecko', tags: 'gecko lizard fast' },
            { ch: '\uD83D\uDC32', name: 'dragon', tags: 'dragon fire powerful' },
            { ch: '\uD83E\uDD84', name: 'unicorn', tags: 'unicorn magical rare' },
            { ch: '\uD83D\uDC1B', name: 'caterpillar', tags: 'caterpillar bug small' },
            { ch: '\uD83E\uDD80', name: 'crab', tags: 'crab seafood' },
            { ch: '\uD83D\uDC19', name: 'octopus', tags: 'octopus tentacles multitask' },
            { ch: '\uD83D\uDC2C', name: 'dolphin', tags: 'dolphin clever ocean' },
            { ch: '\uD83D\uDC0B', name: 'whale', tags: 'whale big ocean' },
            { ch: '\uD83E\uDD9C', name: 'parrot', tags: 'parrot mimic talk' },
            { ch: '\uD83D\uDC0E', name: 'horse', tags: 'horse fast steady' },
        ],
    },
    {
        name: 'Symbols & objects',
        emojis: [
            { ch: '\u2728', name: 'sparkles', tags: 'sparkles ai assistant magic' },
            { ch: '\uD83D\uDE80', name: 'rocket', tags: 'rocket launch fast startup' },
            { ch: '\uD83D\uDCA1', name: 'lightbulb', tags: 'lightbulb idea insight think' },
            { ch: '\uD83D\uDD25', name: 'fire', tags: 'fire hot trending' },
            { ch: '\u26A1', name: 'lightning', tags: 'lightning fast power energy' },
            { ch: '\uD83C\uDF1F', name: 'star', tags: 'star favorite top' },
            { ch: '\uD83C\uDFAF', name: 'target', tags: 'target goal aim focus' },
            { ch: '\uD83D\uDD11', name: 'key', tags: 'key access unlock secret' },
            { ch: '\uD83D\uDD12', name: 'lock', tags: 'lock secure private' },
            { ch: '\uD83D\uDD13', name: 'unlock', tags: 'unlock open access' },
            { ch: '\uD83C\uDFC6', name: 'trophy', tags: 'trophy winner award' },
            { ch: '\uD83C\uDFB2', name: 'dice', tags: 'dice random chance game' },
            { ch: '\uD83C\uDFAC', name: 'clapper', tags: 'clapper film movie director' },
            { ch: '\uD83C\uDFA8', name: 'palette', tags: 'palette art design creative' },
            { ch: '\uD83C\uDFB5', name: 'note', tags: 'music note tune' },
            { ch: '\uD83D\uDD14', name: 'bell', tags: 'bell notification alert' },
            { ch: '\uD83D\uDC8C', name: 'love letter', tags: 'love letter mail message' },
            { ch: '\uD83D\uDCE7', name: 'email', tags: 'email mail inbox' },
            { ch: '\uD83D\uDCAC', name: 'speech', tags: 'speech chat message dialogue' },
            { ch: '\uD83D\uDCAD', name: 'thought', tags: 'thought bubble think idea' },
            { ch: '\uD83D\uDCDA', name: 'books', tags: 'books library reference' },
            { ch: '\uD83C\uDF93', name: 'graduate', tags: 'graduate cap academic' },
            { ch: '\uD83C\uDFAA', name: 'circus', tags: 'circus performance show' },
            { ch: '\uD83E\uDDED', name: 'compass', tags: 'compass direction navigation' },
            { ch: '\uD83E\uDDEC', name: 'dna', tags: 'dna science biology research' },
            { ch: '\uD83E\uDDEA', name: 'test tube', tags: 'test tube lab experiment' },
            { ch: '\uD83D\uDD2E', name: 'crystal ball', tags: 'crystal ball prediction future' },
            { ch: '\uD83D\uDDFA\uFE0F', name: 'map', tags: 'map navigation location' },
            { ch: '\uD83C\uDF0D', name: 'globe', tags: 'globe earth world' },
            { ch: '\uD83C\uDF31', name: 'seedling', tags: 'seedling growth plant new' },
            { ch: '\uD83C\uDF38', name: 'cherry blossom', tags: 'cherry blossom spring beauty' },
            { ch: '\uD83D\uDD78\uFE0F', name: 'web', tags: 'web spider network connect' },
        ],
    },
    {
        name: 'Food & drink',
        emojis: [
            { ch: '\u2615', name: 'coffee', tags: 'coffee cup energy productivity' },
            { ch: '\uD83C\uDF75', name: 'tea', tags: 'tea cup calm zen' },
            { ch: '\uD83C\uDF7A', name: 'beer', tags: 'beer celebrate' },
            { ch: '\uD83C\uDF77', name: 'wine', tags: 'wine glass elegant' },
            { ch: '\uD83C\uDF55', name: 'pizza', tags: 'pizza food slice' },
            { ch: '\uD83C\uDF54', name: 'burger', tags: 'burger food classic' },
            { ch: '\uD83C\uDF2E', name: 'taco', tags: 'taco food mex' },
            { ch: '\uD83C\uDF63', name: 'sushi', tags: 'sushi food japan' },
            { ch: '\uD83C\uDF6A', name: 'cookie', tags: 'cookie sweet snack treat' },
            { ch: '\uD83C\uDF70', name: 'cake', tags: 'cake celebrate dessert' },
            { ch: '\uD83C\uDF66', name: 'icecream', tags: 'ice cream sweet cool' },
            { ch: '\uD83C\uDF49', name: 'watermelon', tags: 'watermelon summer fruit' },
            { ch: '\uD83C\uDF4E', name: 'apple', tags: 'apple fruit teacher' },
            { ch: '\uD83C\uDF4C', name: 'banana', tags: 'banana fruit potassium' },
            { ch: '\uD83C\uDF36\uFE0F', name: 'hot pepper', tags: 'spicy hot pepper' },
        ],
    },
];
export function AvatarPicker({ value, onChange, title, compact = false }) {
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const containerRef = useRef(null);
    const filterInputRef = useRef(null);
    // Outside-click / Escape closes.
    useEffect(() => {
        if (!open)
            return;
        const onDocClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        // Focus the search input when opening.
        requestAnimationFrame(() => filterInputRef.current?.focus());
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);
    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q)
            return CATEGORIES;
        const matched = [];
        for (const cat of CATEGORIES) {
            const hits = cat.emojis.filter((e) => e.name.toLowerCase().includes(q) || e.tags.toLowerCase().includes(q));
            if (hits.length > 0) {
                matched.push({ name: cat.name, emojis: hits });
            }
        }
        return matched;
    }, [filter]);
    const onPick = (ch) => {
        onChange(ch);
        setOpen(false);
        setFilter('');
    };
    return (_jsxs("div", { ref: containerRef, className: `avatar-picker ${compact ? 'compact' : ''}`, children: [_jsxs("button", { type: "button", className: "avatar-picker-trigger", onClick: () => setOpen((o) => !o), title: title ?? 'Pick an avatar emoji', "aria-expanded": open, "aria-label": "Choose emoji", children: [_jsx("span", { className: "avatar-picker-current", children: value || '\uD83E\uDD16' }), _jsx("span", { className: "avatar-picker-caret", "aria-hidden": true, children: '\u25BE' })] }), open && (_jsxs("div", { className: "avatar-picker-popover", role: "dialog", "aria-label": "Emoji picker", children: [_jsx("div", { className: "avatar-picker-search", children: _jsx("input", { ref: filterInputRef, type: "text", value: filter, onChange: (e) => setFilter(e.target.value), placeholder: 'Search emoji\u2026', maxLength: 40 }) }), _jsxs("div", { className: "avatar-picker-body", children: [filtered.length === 0 && (_jsxs("div", { className: "avatar-picker-empty", children: ["No matches for \"", filter, "\"."] })), filtered.map((cat) => (_jsxs("div", { className: "avatar-picker-category", children: [_jsx("div", { className: "avatar-picker-category-name", children: cat.name }), _jsx("div", { className: "avatar-picker-grid", children: cat.emojis.map((e) => (_jsx("button", { type: "button", className: `avatar-picker-cell ${e.ch === value ? 'selected' : ''}`, onClick: () => onPick(e.ch), title: e.name, children: e.ch }, e.ch))) })] }, cat.name)))] })] }))] }));
}
